import React, { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useEmployeeRegisterMutation } from '../../queries/auth';
import AuthShell from '../../components/auth/AuthShell';
import { ErrorBanner, PasswordField, Head, Submit, focusFirstInvalid } from '../../components/auth/parts';
import { classify, firstError } from '../../components/auth/authErrors';
import { MIN_PASSWORD } from '../../components/auth/validators';
import useCountdown from '../../components/auth/useCountdown';
import useAuthTitle from '../../components/auth/useAuthTitle';

// Invite acceptance: choose a password, land in the dashboard.
const EmployeeSignUp = () => {
  const { t } = useTranslation();
  useAuthTitle('invite');
  const a = (k) => t(`authPage.${k}`);
  const { invitation_id } = useParams();

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [errs, setErrs] = useState({});
  const [banner, setBanner] = useState(null); // rate | offline | server
  const formRef = useRef(null);
  const cd = useCountdown();
  const register = useEmployeeRegisterMutation({ inline: true });
  const locked = banner === 'rate' && cd.left > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (locked || register.isLoading) return;
    const n = {};
    if (!password) n.password = a('eReq'); else if (password.length < MIN_PASSWORD) n.password = a('eShort');
    if (!password2) n.password2 = a('eReq'); else if (password2 !== password) n.password2 = a('eMatch');
    setErrs(n);
    if (Object.keys(n).length) { focusFirstInvalid(formRef.current); return; }

    setBanner(null);
    try {
      // Same body as before: { password, password_confirmation }.
      await register.mutateAsync({ invitationId: invitation_id, data: { password, password_confirmation: password2 } });
    } catch (err) {
      const info = classify(err);
      if (info.kind === 'rate') { cd.start(info.retryAfter); setBanner('rate'); }
      else if (info.kind === 'offline') setBanner('offline');
      else if (info.kind === 'validation' && firstError(info.errors, 'password')) {
        setErrs({ password: /confirm/i.test(firstError(info.errors, 'password')) ? '' : a('eShort'), password2: /confirm/i.test(firstError(info.errors, 'password')) ? a('eMatch') : '' });
      } else setBanner('server');
    }
  };

  return (
    <AuthShell>
      <Head icon="team" title={t('authPage.invite.h')} text={t('authPage.invite.p')} />
      <form ref={formRef} noValidate onSubmit={onSubmit}>
        {banner && <ErrorBanner kind={banner} left={cd.left} />}
        <PasswordField id="password" label={a('pw')} autoComplete="new-password" value={password}
          onChange={(e) => { setPassword(e.target.value); setErrs((x) => ({ ...x, password: '' })); }} error={errs.password} checklist />
        <PasswordField id="password2" label={a('pw2')} autoComplete="new-password" value={password2}
          onChange={(e) => { setPassword2(e.target.value); setErrs((x) => ({ ...x, password2: '' })); }} error={errs.password2} />
        <Submit busy={register.isLoading} busyLabel={t('authPage.invite.busy')} disabled={locked}>{t('authPage.invite.btn')}</Submit>
      </form>
    </AuthShell>
  );
};

export default EmployeeSignUp;
